/**
 * Browser sign-in.
 *
 * Google: `startGoogleLogin()` navigates to the backend's
 * `GET /api/auth/google/start/?return_to=…`; after consent the backend
 * redirects to `<return_to>#access_token=…&refresh_token=…&user_id=…&user_name=…&user_email=…`
 * (or `#error=…`). `completeLoginFromUrl()` reads that fragment on load,
 * stores the tokens and removes the fragment from the address bar.
 *
 * QA: on a dev origin, `?qa=<email>` (or `?qa=1` for the default service
 * account) logs in through the DEBUG-only `/api/auth/service-account/`.
 */

import { useEffect, useState } from 'react';
import { ApiClient, getApi, type Session } from './api';
import { appBase, isDevOrigin, SERVICE_ACCOUNT_KEY } from './env';

export const DEFAULT_QA_EMAIL = 'service-account@pdfcollab.test';

/** Where the OAuth callback should land: absolute on dev origins (allowed by
 *  the backend's WEB_DEV_ORIGINS when DEBUG), a path in production. */
export function defaultReturnTo(): string {
  const base = appBase();
  if (isDevOrigin() && typeof location !== 'undefined') return `${location.origin}${base}`;
  return base;
}

export function startGoogleLogin(returnTo: string = defaultReturnTo(), api: ApiClient = getApi()): void {
  const url = api.url(`/api/auth/google/start/?return_to=${encodeURIComponent(returnTo)}`);
  location.assign(url);
}

export type FragmentResult = { kind: 'none' } | { kind: 'ok'; session: Session } | { kind: 'error'; error: string };

/** Parse an OAuth result fragment (`#access_token=…` / `#error=…`). Pure. */
export function parseAuthFragment(hash: string): FragmentResult {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!h) return { kind: 'none' };
  const p = new URLSearchParams(h);
  const error = p.get('error');
  if (error) return { kind: 'error', error };
  const access = p.get('access_token');
  const refresh = p.get('refresh_token');
  const id = p.get('user_id');
  if (!access && !refresh) return { kind: 'none' };
  if (!access || !refresh || !id) return { kind: 'error', error: 'Sign-in response was missing tokens' };
  return {
    kind: 'ok',
    session: { access, refresh, user: { id, name: p.get('user_name') ?? '', email: p.get('user_email') ?? '' } },
  };
}

/** Consume the fragment on page load. Stores tokens and cleans the URL. */
export function completeLoginFromUrl(api: ApiClient = getApi()): FragmentResult {
  if (typeof location === 'undefined') return { kind: 'none' };
  const res = parseAuthFragment(location.hash);
  if (res.kind === 'none') return res;
  if (res.kind === 'ok') api.setSession(res.session);
  // Tokens must never linger in history / the address bar.
  history.replaceState(history.state, '', location.pathname + location.search);
  return res;
}

/** `?qa=` value when QA login is allowed on this origin, else null. */
export function qaEmailFromUrl(): string | null {
  if (typeof location === 'undefined' || !isDevOrigin()) return null;
  const v = new URLSearchParams(location.search).get('qa');
  if (!v) return null;
  return v.includes('@') ? v : DEFAULT_QA_EMAIL;
}

export async function qaLogin(email: string = DEFAULT_QA_EMAIL, api: ApiClient = getApi()): Promise<Session> {
  const name = email === DEFAULT_QA_EMAIL ? 'Service Account' : email.split('@')[0].replace(/[._-]+/g, ' ');
  return api.serviceAccountLogin(email, name, SERVICE_ACCOUNT_KEY);
}

function stripQaParam(): void {
  const url = new URL(location.href);
  url.searchParams.delete('qa');
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
}

export type AuthStatus = 'loading' | 'signed_in' | 'signed_out';

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  error: string | null;
  signOut(): void;
}

/**
 * Boot-time auth: consume an OAuth fragment, else run QA login if requested,
 * else use stored tokens. Re-renders on sign-in / sign-out.
 */
export function useAuth(api: ApiClient = getApi()): AuthState {
  const [session, setSession] = useState<Session | null>(() => api.tokens.session);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const off = api.onSessionChange((s) => {
      setSession(s);
      setStatus(s ? 'signed_in' : 'signed_out');
    });
    let cancelled = false;
    (async () => {
      const frag = completeLoginFromUrl(api);
      if (frag.kind === 'error') setError(`Sign-in failed: ${frag.error}`);
      const qa = qaEmailFromUrl();
      if (qa) {
        try {
          await qaLogin(qa, api);
          stripQaParam();
        } catch (e) {
          if (!cancelled) setError(`QA login failed: ${(e as Error).message}`);
        }
      }
      if (cancelled) return;
      const s = api.tokens.session;
      setSession(s);
      setStatus(s ? 'signed_in' : 'signed_out');
    })();
    return () => {
      cancelled = true;
      off();
    };
  }, [api]);

  return { status, session, error, signOut: () => api.signOut() };
}
